import React, { useMemo } from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import classnames from 'classnames';
import { Badge } from '@/components/common/Badge';
import { Icon, IconProps, IconSize } from '@/components/common/Icon';
import { Tooltip } from '@/components/common/Tooltip';
import { PageHref } from '@/constants';
import { NavigationRoute } from '@/lib/navigationRoutes';
import { Colors } from '@/styles/colors';
import styles from './Link.module.scss';

interface LinkProps {
  title?: string;
  icon: IconProps['name'];
  href: string;
  disabled?: boolean;
  isActive?: NavigationRoute['isActiveLink'];
  isBeta?: NavigationRoute['isBeta'];
  isOpen?: boolean;
}

export const Link: React.FC<LinkProps> = ({ icon, isBeta, title, href, disabled, isActive, isOpen }) => {
  const pathname = usePathname();
  const getIsActiveLink = (href: string) => href === pathname;
  const isActiveLink = isActive ? isActive(pathname as PageHref) : getIsActiveLink(href);
  const betaBadgeComponent = useMemo(
    () => (
      <Badge
        className={classnames(styles.betaBadge, { [styles.normal]: isOpen, [styles.buttonBadge]: !isOpen })}
        label="beta"
      />
    ),
    [isOpen],
  );

  return (
    <Tooltip label={title} placement="right" isDisabled={isOpen || !title}>
      <NextLink
        href={disabled ? '#' : href}
        className={classnames(styles.root, {
          [styles.disabled]: disabled,
          [styles.active]: isActiveLink,
        })}
        {...(isOpen ? { title } : {})}
      >
        <div className={styles.iconWrapper}>
          {isBeta && !isOpen && betaBadgeComponent}
          <Icon name={icon} size={IconSize.s24} fillColor={isActiveLink ? Colors.WhiteStatic : Colors.Neutral} />
        </div>
        {isOpen && (
          <div className={classnames('flex w-full items-center justify-between gap-[8px]', styles.titleWrapper)}>
            <div className={styles.title}>{title}</div>
            {isBeta && betaBadgeComponent}
          </div>
        )}
      </NextLink>
    </Tooltip>
  );
};
